'use client';

import { useEffect, useState } from 'react';

import { Spinner } from './Spinner';
import { ApiClientError, createApiClient } from '@/lib/api';
import { getInitData } from '@/lib/tma';

interface Entry {
  rank: number;
  telegram_id: number;
  username: string | null;
  first_name: string;
  total_profit_usd_cents: number;
  bets_count: number;
  wins_count: number;
}

interface Me {
  rank: number | null;
  total: number;
  telegram_id: number;
  first_name: string;
  username: string | null;
  total_profit_usd_cents: number;
  bets_count: number;
  wins_count: number;
}

export function LeaderboardMini({ refreshKey }: { refreshKey: number }) {
  const [top, setTop] = useState<Entry[] | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const api = createApiClient(getInitData);
    api
      .leaderboard('all_time', 5)
      .then((r) => {
        setTop(r.top);
        setMe(r.me);
      })
      .catch((err) => {
        const e = err as ApiClientError;
        console.error('LeaderboardMini failed:', e?.code);
      })
      .finally(() => setLoading(false));
  }, [refreshKey]);

  if (loading) {
    return (
      <div className="bg-bg-elev p-5 mb-4 flex justify-center">
        <Spinner size={20} />
      </div>
    );
  }

  if (!top || top.length === 0) {
    return (
      <div className="bg-bg-elev p-5 mb-4 border-l-[3px] border-accent">
        <div className="font-mono text-[10px] text-text-tertiary tracking-[0.15em] mb-2">
          ● ЛИДЕРБОРД
        </div>
        <p className="text-text-tertiary text-sm">
          Пока никто не зарезолвил ни одной ставки. Будь первым.
        </p>
      </div>
    );
  }

  const myInTop = me?.rank !== null && top.some((t) => t.telegram_id === me?.telegram_id);

  return (
    <div className="bg-bg-elev p-5 mb-4 border-l-[3px] border-accent">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[10px] text-text-tertiary tracking-[0.15em]">
          ● ЛИДЕРБОРД
        </div>
        {me?.rank !== null && me !== null && (
          <span className="font-mono text-[10px] text-accent tracking-[0.05em] tabular-nums">
            #{me.rank} ИЗ {me.total}
          </span>
        )}
      </div>

      <div className="space-y-1.5">
        {top.map((entry) => (
          <Row
            key={entry.telegram_id}
            rank={entry.rank}
            name={entry.first_name}
            username={entry.username}
            profitCents={entry.total_profit_usd_cents}
            betsCount={entry.bets_count}
            isMe={me?.telegram_id === entry.telegram_id}
          />
        ))}

        {!myInTop && me && me.rank !== null && (
          <>
            <div className="text-text-tertiary text-center font-mono text-[10px] py-1">···</div>
            <Row
              rank={me.rank}
              name={me.first_name}
              username={me.username}
              profitCents={me.total_profit_usd_cents}
              betsCount={me.bets_count}
              isMe
            />
          </>
        )}

        {!me && (
          <p className="font-mono text-[10px] text-text-tertiary mt-3 text-center tracking-[0.05em]">
            ПЕРВАЯ РЕЗОЛВЛЁННАЯ СТАВКА → ПОПАДАЕШЬ В РЕЙТИНГ
          </p>
        )}
      </div>
    </div>
  );
}

function Row({
  rank,
  name,
  username,
  profitCents,
  betsCount,
  isMe,
}: {
  rank: number;
  name: string;
  username: string | null;
  profitCents: number;
  betsCount: number;
  isMe: boolean;
}) {
  const profit = profitCents / 100;
  return (
    <div
      className={`flex items-center justify-between gap-3 px-3 py-2 ${
        isMe ? 'bg-accent/10 border border-accent/30' : ''
      }`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span
          className={`font-mono text-[11px] tabular-nums w-7 ${
            rank === 1 ? 'text-accent font-bold' : 'text-text-tertiary'
          }`}
        >
          {rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : `#${rank}`}
        </span>
        <div className="min-w-0">
          <p className={`text-[13px] leading-tight truncate ${isMe ? 'text-accent font-bold' : ''}`}>
            {isMe ? 'ТЫ' : name}
          </p>
          <p className="font-mono text-[10px] text-text-tertiary truncate tracking-[0.05em]">
            {username ? `@${username}` : '—'} · {betsCount} ставок
          </p>
        </div>
      </div>
      <span
        className={`text-[13px] font-bold tabular-nums whitespace-nowrap ${
          profit > 0 ? 'text-yes' : profit < 0 ? 'text-no' : 'text-text-secondary'
        }`}
      >
        {profit >= 0 ? '+' : '−'}${Math.abs(profit).toFixed(2)}
      </span>
    </div>
  );
}
